/**
 * Zod schemas dùng chung cho tRPC routers
 * Tương đương với DTO, sử dụng Zod để validate input
 */

import { z } from 'zod';
import {
  CustomerCreateInputSchema,
  CustomerSchema,
  MachineTypeEnumSchema,
  SpecStatusEnumSchema,
  ToolingStatusEnumSchema,
} from '@apv-erp/status/generated';

// --- Pagination ---
// passthrough: giữ lại các tham số lọc theo cột (vd: name, code, status...) do từng service tự đọc,
// tránh việc zod âm thầm loại bỏ khiến bộ lọc trên giao diện không có tác dụng.
export const paginationSchema = z.object({
  page: z.number().int().min(1).optional().default(1),
  limit: z.number().int().min(1).max(10000).optional().default(20),
  search: z.string().nullable().optional(),
  sortBy: z.string().nullable().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  isDeleted: z.boolean().nullable().optional(),
}).passthrough();

// --- Common ---
export const idSchema = z.object({
  id: z.string().min(1),
});

export const statusUpdateSchema = z.object({
  id: z.string().min(1),
  status: z.string().min(1),
});

// --- Auth ---
export const loginSchema = z.object({
  email: z.string().min(1, "Vui lòng nhập email hoặc tên đăng nhập"),
  password: z.string().min(1),
});

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z.object({
  email: z.string().email(),
  otp: z.string().min(6),
  password: z.string().min(6),
});

// --- Customer ---
export const queryCustomerSchema = paginationSchema.extend({
  type: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  group: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  shortName: z.string().nullable().optional(),
  taxCode: z.string().nullable().optional(),
  contactPerson: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  representative: z.string().nullable().optional(),
  salesUserId: z.string().nullable().optional(),
  salesName: z.string().nullable().optional(),
  hasDeliverableOrders: z.boolean().nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  rank6m: z.string().nullable().optional(),
  debtStatus: z.string().nullable().optional(),
});

export const adjustCustomerDebtSchema = z.object({
  customerId: z.string().min(1),
  newCurrentDebt: z.number().min(0, 'Công nợ hiện tại không thể âm'),
  reason: z.string().min(3, 'Vui lòng nhập lý do điều chỉnh công nợ'),
});

// Dẫn xuất từ DB: prisma/schema.prisma -> CustomerCreateInputSchema (sinh tự động).
// `pick` là whitelist các cột client được ghi. Cột sổ cái (currentDebt, openingDebt, orderValue,
// deliveryValue, paidAmount, remainingDebt) do hệ thống tự tích lũy nên KHÔNG được pick.
// Nếu cột bị xóa/đổi tên trong DB, `pick` sẽ lỗi biên dịch -> không thể lệch âm thầm.
export const createCustomerSchema = CustomerCreateInputSchema.pick({
  code: true,
  name: true,
  shortName: true,
  type: true,
  group: true,
  representative: true,
  address: true,
  deliveryAddress: true,
  phone: true,
  mobile: true,
  email: true,
  taxCode: true,
  contactPerson: true,
  bankName: true,
  bankAccountNumber: true,
  bankAccountHolder: true,
  paymentTerms: true,
  deliveryMethod: true,
  transitAddress: true,
  website: true,
  fanpage: true,
  logo: true,
  position: true,
  colorTolerance: true,
  quantityTolerance: true,
  tolerancePercent: true,
  notes: true,
  tags: true,
  creditLimit: true,
  initialDebt: true,
  salesUserId: true,
  status: true,
}).extend({
  // DB bắt buộc `code`, nhưng API cho phép bỏ trống để service tự sinh mã khách hàng
  code: CustomerSchema.shape.code.nullable().optional(),
  // Rule nghiệp vụ (DB chỉ biết "NOT NULL")
  name: CustomerSchema.shape.name.min(1),
  phone: CustomerSchema.shape.phone.optional().or(z.literal('')),
  email: CustomerSchema.shape.email.unwrap().email().optional().or(z.literal('')).nullable(),
  tolerancePercent: CustomerSchema.shape.tolerancePercent.min(0).max(10).optional().default(1.0),
  creditLimit: CustomerSchema.shape.creditLimit.optional().default(0),
  initialDebt: CustomerSchema.shape.initialDebt.nullable().optional().default(0),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional().default('ACTIVE'),
});

export const updateCustomerSchema = createCustomerSchema.partial().extend({
  id: z.string().min(1),
});

// --- Product ---
export const queryProductSchema = paginationSchema.extend({
  type: z.string().nullable().optional(),
  typeId: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  createdDateFrom: z.string().nullable().optional(),
  createdDateTo: z.string().nullable().optional(),
});

export const createProductSchema = z.object({
  productCode: z.string().optional(),
  productName: z.string().min(1),
  customerId: z.string().nullable().optional(),
  typeId: z.string().nullable().optional(),
  materialId: z.string().nullable().optional(),
  active: z.boolean().default(true),
  specs: z.any().nullable().optional(),
  length: z.number().nullable().optional(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  flatLength: z.number().nullable().optional(),
  flatWidth: z.number().nullable().optional(),
  printFormat: z.string().nullable().optional(),
  cutFormat: z.string().nullable().optional(),
  outs: z.number().int().nullable().optional(),
  standardCost: z.number().nullable().optional(),
  packingSpec: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  pcsPerBox: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? null : Number(v)),
    z.number().int().min(1, 'Số cái / thùng phải lớn hơn 0').nullable().optional(),
  ),
  unit: z.string().nullable().optional(),
  price: z.number().nullable().optional(),
  costPrice: z.number().nullable().optional(),
  process: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  tags: z.string().nullable().optional(),
  customerCode: z.string().nullable().optional(),
  isComposite: z.boolean().default(false),
  materialIds: z.array(z.string()).optional(),
  productMaterials: z.array(z.any()).optional(),
  processIds: z.array(z.string()).optional(),
  processConfigs: z.array(z.any()).optional(),
  components: z.array(z.any()).optional(),
});

export const updateProductSchema = z.object({
  id: z.string().min(1),
  // Người dùng đã xác nhận sửa BOM / quy trình khi sản phẩm còn Lệnh SX đang chạy (hộp thoại cảnh báo ở form sản phẩm)
  data: createProductSchema.partial().extend({ _bypassActivePoCheck: z.boolean().optional() })
});

export const duplicateProductSchema = z.object({
  sourceId: z.string().min(1),
  newProductName: z.string().min(1, 'Tên sản phẩm mới không được để trống'),
  newProductCode: z.string().optional(),
  customerId: z.string().optional(),
  keepSharedMold: z.boolean().default(true),
});


// --- Material ---
export const queryMaterialSchema = paginationSchema.extend({
  category: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  materialId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  gsm: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  'vendor.shortName': z.string().nullable().optional(),
  typeId: z.union([z.string(), z.array(z.string())]).nullable().optional(),
});

/**
 * Số nhận từ client có thể là chuỗi số (vd: Decimal từ DB trả về "5000.00") hoặc chuỗi rỗng.
 * Ép về number; chuỗi rỗng/null -> null; chuỗi không phải số -> giữ nguyên để zod báo lỗi.
 */
const looseNumber = (inner: z.ZodNumber) =>
  z.preprocess((v) => {
    if (v === undefined || v === null) return v;
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed === '') return null;
      const num = Number(trimmed);
      return Number.isNaN(num) ? v : num;
    }
    return v;
  }, inner.nullable().optional());

export const createMaterialSchema = z.object({
  materialId: z.string().nullable().optional(),
  name: z.string().min(1),
  typeId: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  productionOrderId: z.string().nullable().optional(),
  category: z.enum(['PAPER', 'INK', 'GLUE', 'FILM', 'CHEMICAL', 'PLATE', 'OTHER']).nullable().optional(),
  subType: z.string().nullable().optional(),
  specs: z.string().nullable().optional(),
  gsm: z.string().nullable().optional(),
  width: looseNumber(z.number().min(0, 'Chiều rộng phải lớn hơn hoặc bằng 0')),
  length: looseNumber(z.number().min(0, 'Chiều dài phải lớn hơn hoặc bằng 0')),
  unit: z.string().nullable().optional(),
  initialStock: looseNumber(z.number().min(0, 'Tồn ban đầu phải lớn hơn hoặc bằng 0')),
  currentStock: looseNumber(z.number().min(0, 'Tồn hiện tại phải lớn hơn hoặc bằng 0')),
  minStock: looseNumber(z.number().int().min(0, 'Tồn tối thiểu phải lớn hơn hoặc bằng 0')),
  unitPrice: looseNumber(z.number().min(0, 'Đơn giá phải lớn hơn hoặc bằng 0')),
  image: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  height: looseNumber(z.number().min(0, 'Chiều cao phải lớn hơn hoặc bằng 0')),
});

export const updateMaterialSchema = z.preprocess((val: any) => {
  if (val && typeof val === 'object') {
    // Nếu client đã truyền đúng dạng chuẩn { id, data: {...} }
    if (val.id && val.data && typeof val.data === 'object') {
      return val;
    }
    // Nếu client truyền dạng phẳng: { materialId, ...rest } hoặc { id, ...rest }
    const resolvedId = val.id || val.materialId;
    if (resolvedId) {
      const { id: _id, materialId: _mId, data, ...rest } = val;
      return {
        id: String(resolvedId),
        data: data && typeof data === 'object' ? { ...data, ...rest } : rest,
      };
    }
  }
  return val;
}, z.object({
  id: z.string().min(1, 'ID vật tư không được để trống'),
  data: createMaterialSchema.partial(),
}));


// --- User ---
export const queryUserSchema = paginationSchema.extend({
  role: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  department: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  section: z.string().nullable().optional(),
  employeeCode: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
});

// --- Employee Contract ---
export const queryEmployeeContractSchema = paginationSchema.extend({
  userId: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
});

export const createEmployeeContractSchema = z.object({
  userId: z.string().min(1),
  contractNumber: z.string().min(1),
  contractType: z.string().min(1),
  startDate: z.union([z.string(), z.date()]),
  endDate: z.union([z.string(), z.date()]).nullable().optional(),
  basicSalary: z.number().nullable().optional(),
  fileUrl: z.string().nullable().optional(),
  status: z.string().min(1),
});

export const updateEmployeeContractSchema = z.object({
  id: z.string().min(1),
  data: createEmployeeContractSchema.partial()
});



// --- Machine ---
export const queryMachineSchema = paginationSchema.extend({
  type: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  machineId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
});

// --- Quotation ---
export const queryQuotationSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  customerId: z.string().nullable().optional(),
  salesUserId: z.string().nullable().optional(),
  creatorId: z.string().nullable().optional(),
  createdDateFrom: z.string().nullable().optional(),
  createdDateTo: z.string().nullable().optional(),
  validUntilFrom: z.string().nullable().optional(),
  validUntilTo: z.string().nullable().optional(),
  // Added for inline filters
  quotationId: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  productName: z.string().nullable().optional(),
  contactPerson: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

// --- Order ---
export const queryOrderSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  customerId: z.string().nullable().optional(),
  quotationId: z.string().nullable().optional(),
  salesUserId: z.string().nullable().optional(),
  orderDateFrom: z.string().nullable().optional(),
  orderDateTo: z.string().nullable().optional(),
  deliveryDeadlineFrom: z.string().nullable().optional(),
  deliveryDeadlineTo: z.string().nullable().optional(),
  // Added for inline filters
  orderId: z.string().nullable().optional(),
  poNumber: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  productName: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  hasAvailableFG: z.boolean().nullable().optional(),
  priority: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  isUrgentOnly: z.union([z.boolean(), z.string()]).nullable().optional(),
  workflowTab: z.string().nullable().optional(),
  deadlineFilter: z.string().nullable().optional(),
  // Tab "Đơn hàng" của sản phẩm: chỉ đơn có dòng thuộc sản phẩm này
  productId: z.string().nullable().optional(),
});

// --- Production Order ---
export const queryProductionOrderSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  priority: z.string().nullable().optional(),
  salesOrderId: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  plannedEndFrom: z.string().nullable().optional(),
  plannedEndTo: z.string().nullable().optional(),
  // Added for inline filters
  orderId: z.string().nullable().optional(),
  poReference: z.string().max(50, "Tối đa 50 ký tự").nullable().optional(),
  productName: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  materialStatus: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  toolingStatus: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  prepressStatus: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  urgencyFilter: z.enum(['urgent', 'high', 'approaching', 'overdue']).nullable().optional(),
});

export const addComponentSchema = z.object({
  componentName: z.string().min(1, "Vui lòng nhập tên thành phần"),
  isMain: z.boolean().default(false),
  techSpecs: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const addRoutingStepSchema = z.object({
  componentId: z.string().nullable().optional(),
  stepOrder: z.coerce.number().min(1),
  stepId: z.string().min(1, "Vui lòng chọn hoặc nhập mã công đoạn"),
  name: z.string().min(1, "Vui lòng nhập tên công đoạn"),
  resourceId: z.string().nullable().optional(),
  machineId: z.string().nullable().optional(),
  operatorId: z.string().nullable().optional(),
  toolId: z.string().nullable().optional(),
  inputs: z.array(z.string()).default([]),
  standardQuantity: z.coerce.number().nullable().optional(),
  estDurationHours: z.coerce.number().nullable().optional(),
  overlappingMinutes: z.coerce.number().nullable().optional(),
  overlapPercent: z.coerce.number().nullable().optional(),
  plannedRunWastePct: z.coerce.number().nullable().optional(),
  plannedSetupTime: z.coerce.number().nullable().optional(),
  plannedSpeed: z.coerce.number().nullable().optional(),
  plannedTotalTime: z.coerce.number().nullable().optional(),
  plannedSetupWaste: z.coerce.number().nullable().optional(),
  status: z.string().nullable().optional(),
  technicalRequirements: z.string().nullable().optional(),
  plannedStartDate: z.date().nullable().optional(),
});

export const addToolingSchema = z.object({
  toolingType: z.string().min(1, "Vui lòng chọn loại khuôn/bản in"),
  toolingId: z.string().min(1, "Vui lòng chọn khuôn/bản in"),
  quantity: z.coerce.number().min(1).default(1),
  notes: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  // Thông số khai báo trực tiếp trên dòng khuôn/kẽm của lệnh
  moldCode: z.string().nullable().optional(),
  dimensions: z.string().nullable().optional(),
  processName: z.string().nullable().optional(),
  dieCutStyle: z.string().nullable().optional(),
  plateColor: z.string().nullable().optional(),
  plateSize: z.string().nullable().optional(),
  plateSource: z.string().nullable().optional(),
  expectedDate: z.string().nullable().optional(),
});

export const outsourceToolingSchema = z.object({
  toolingId: z.string().min(1, "Vui lòng chọn khuôn cần gia công"),
  vendorId: z.string().min(1, "Vui lòng chọn nhà cung cấp / đối tác làm khuôn"),
  unitPrice: z.coerce.number().min(0, "Đơn giá phải lớn hơn hoặc bằng 0"),
  expectedDate: z.string().min(1, "Vui lòng chọn ngày hẹn giao khuôn"),
  notes: z.string().nullable().optional(),
});

export const addBomItemSchema = z.object({
  componentId: z.string().nullable().optional(),
  materialId: z.string().min(1, "Vui lòng chọn vật tư"),
  qtyRequired: z.coerce.number().min(0.001, "Số lượng phải lớn hơn 0"),
  wastagePercent: z.coerce.number().default(0),
  routingStepId: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  specification: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export const auxiliaryBomItemSchema = z.object({
  materialId: z.string().min(1),
  routingStepId: z.string().nullable().optional(),
  processType: z.string().nullable().optional(), // 'CAN_MANG', 'EP_KIM', 'DAN_HOP', 'PHU_UV'
  stepId: z.string().nullable().optional(),
  stepName: z.string().nullable().optional(),
  qtyRequired: z.coerce.number().positive(),
  unit: z.string().default('Tờ'),
  specification: z.string().nullable().optional(),
  wastagePercent: z.coerce.number().default(5),
  shortEdgeCm: z.number().optional(),
  is2Sides: z.boolean().optional(),
  meters: z.number().optional(),
  areaM2: z.number().optional(),
  notes: z.string().nullable().optional(),
});

// --- Machine Log ---
export const queryMachineLogSchema = paginationSchema.extend({
  machineId: z.string().nullable().optional(),
  productionOrderId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
});

// Mã nhật ký (LOG-YYYY-xxxxx) do máy chủ sinh (nextDocumentCode) — client không gửi.
// type khớp enum DB MachineLogType (trước đây cho QC/OTHER → lỗi 500 khi ghi).
export const createMachineLogSchema = z.object({
  machineId: z.string().min(1, 'Vui lòng chọn máy'),
  productionOrderId: z.string().nullable().optional(),
  date: z.union([z.string(), z.date()]),
  shift: z.string().nullable().optional(),
  type: z.enum(['OPERATION', 'DOWNTIME', 'MAINTENANCE', 'SETUP']),
  status: z.string().nullable().optional(),
  durationMinutes: z.number().int().min(0, 'Thời gian (phút) không được âm').nullable().optional(),
  parameters: z.any().nullable().optional(),
  issueType: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  fixAction: z.string().nullable().optional(),
  operatorId: z.string().nullable().optional(),
});

export const updateMachineLogSchema = z.object({
  id: z.string().min(1),
  data: createMachineLogSchema.partial()
});


// --- QC ---
export const queryQcSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  packingStatus: z.enum(['ALL', 'PENDING_PACKING', 'PACKED_INWARD']).nullable().optional(),
  search: z.string().nullable().optional(),
  materialId: z.string().nullable().optional(),
  productionOrderId: z.string().nullable().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
});

export const queryQcProcessSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  productionOrderId: z.string().nullable().optional(),
  routingStepId: z.string().nullable().optional(),
  checkType: z.string().nullable().optional(),
  search: z.string().nullable().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
});

export const queryNcrSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  severity: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  search: z.string().nullable().optional(),
});

// --- Vendor ---
export const queryVendorSchema = paginationSchema.extend({
  type: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  shortName: z.string().nullable().optional(),
  taxCode: z.string().nullable().optional(),
  contactPerson: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  representative: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  // Màn Công nợ NCC: trả kèm đầu kỳ / phát sinh / đã trả / cuối kỳ tính từ sổ công nợ máy chủ
  includeDebt: z.boolean().optional(),
});

/** SĐT chỉ gồm chữ số và các ký tự định dạng thông dụng (+ - . ( ) khoảng trắng), không chứa chữ cái */
const lenientPhone = z
  .string()
  .max(20, 'Số điện thoại tối đa 20 ký tự')
  .refine((v) => v === '' || /^\+?[0-9\s\-().]{6,20}$/.test(v), {
    message: 'Số điện thoại không hợp lệ (chỉ gồm chữ số, không chứa chữ cái)',
  })
  .nullable()
  .optional();

export const createVendorSchema = z.object({
  code: z.string().min(1),
  type: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
  shortName: z.string().nullable().optional(),
  name: z.string().min(1),
  address: z.string().nullable().optional(),
  taxCode: z.string().nullable().optional(),
  representative: z.string().nullable().optional(),
  phone: lenientPhone,
  website: z.string().nullable().optional(),
  fanpage: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  panel: z.string().nullable().optional(),
  bankAccountNumber: z.string().nullable().optional(),
  bankAccountHolder: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  contactPerson: z.string().nullable().optional(),
  mobile: lenientPhone,
  email: z.string().nullable().optional(),
  deliveryMethod: z.string().nullable().optional(),
  deliveryAddress: z.string().nullable().optional(),
  transitAddress: z.string().nullable().optional(),
  initialDebt: z.number().nullable().optional(),
  currentDebt: z.number().nullable().optional(),
  openingDebt: z.number().nullable().optional(),
  orderValue: z.number().nullable().optional(),
  deliveryValue: z.number().nullable().optional(),
  paidAmount: z.number().nullable().optional(),
  remainingDebt: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  tags: z.string().nullable().optional(),
  status: z.string().default('ACTIVE'),
});

export const updateVendorSchema = z.object({
  id: z.string().min(1),
  data: createVendorSchema.partial()
});


// --- Outsource PO ---
export const queryOutsourcePOSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  vendorId: z.string().nullable().optional(),
  issueDateFrom: z.string().nullable().optional(),
  issueDateTo: z.string().nullable().optional(),
  poId: z.string().nullable().optional(),
  vendorName: z.string().nullable().optional(),
});

export const dispatchOutsourcePOSchema = z.object({
  id: z.string().min(1),
  qtyDispatched: z.number().min(1),
  dispatchDate: z.string().optional(),
  notes: z.string().nullable().optional(),
  createInventoryIssue: z.boolean().default(false).optional(),
});

export const receivePOSchema = z.object({
  id: z.string().min(1),
  qtyReceived: z.number().min(1),
  qtyAccepted: z.number().min(0).optional(),
  qtyRejected: z.number().min(0).optional(),
  receiptDate: z.string().optional(),
  defectReason: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  autoReceiveToWip: z.boolean().default(false).optional(),
});

// --- Inventory ---


export const queryInventorySchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  category: z.string().nullable().optional(),
  lastUpdatedFrom: z.string().nullable().optional(),
  lastUpdatedTo: z.string().nullable().optional(),
  search: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  itemType: z.string().nullable().optional(),
  itemId: z.string().nullable().optional(),
  showZeroBalance: z.boolean().nullable().optional(),
  warehouseId: z.string().nullable().optional(),
});

export const inventoryReportSchema = z.object({
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
}).optional();

// --- Purchase Order ---

export const purchaseOrderItemSchema = z.object({
  id: z.string().optional(),
  materialId: z.string().min(1, "Vật tư không được để trống"),
  quantity: z.number().gt(0, "Số lượng phải > 0"), // mua theo kg / m² nhận số lẻ (VD 0,5)
  unitPrice: z.number().min(0, "Đơn giá phải >= 0"),
  productionOrderId: z.string().nullable().optional(),
  materialPlanningId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export const reportFinishedGoodSchema = z.object({
  orderDetailId: z.string().min(1, "Vui lòng chọn sản phẩm"),
  productionOrderId: z.string().min(1, "Thiếu thông tin lệnh sản xuất"),
  quantity: z.coerce.number().min(1, "Số lượng phải lớn hơn 0"),
  machineId: z.string().min(1, "Vui lòng chọn máy móc"),
  employeeId: z.string().min(1, "Vui lòng chọn nhân viên"),
  date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export const createPurchaseOrderSchema = z.object({
  poId: z.string().optional(),
  /** Lưu nháp / Lưu (plan-draft-and-save-buttons-v1) */
  saveMode: z.enum(['DRAFT', 'SUBMIT']).optional(),
  vendorId: z.string().min(1, "Nhà cung cấp không được để trống"),
  // Backend tự tính tổng tiền từ các dòng (calcPurchaseOrderTotal); form không gửi → không bắt buộc
  totalAmount: z.number().min(0).optional(),
  orderDate: z.string().or(z.date()),
  expectedDate: z.string().or(z.date()).nullable().optional(),
  notes: z.string().nullable().optional(),
  items: z.array(purchaseOrderItemSchema).min(1, "Cần ít nhất 1 mặt hàng"),
});

export const updatePurchaseOrderSchema = createPurchaseOrderSchema.partial().extend({
  items: z.array(purchaseOrderItemSchema).optional(),
});

export const inventoryTransactionSchema = z.object({
  itemType: z.enum(['MATERIAL', 'FG', 'PRODUCT']).optional(),
  itemId: z.string().min(1, "Item ID không được để trống"),
  transactionType: z.enum(['RECEIPT', 'ISSUE', 'ADJUSTMENT', 'RETURN']),
  quantity: z.number().min(0),
  warehouseId: z.string().nullable().optional(),
  referenceId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  batchNumber: z.string().nullable().optional(),
});

export const queryPurchaseOrderSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  vendorId: z.string().nullable().optional(),
  orderDateFrom: z.string().nullable().optional(),
  orderDateTo: z.string().nullable().optional(),
  poId: z.string().nullable().optional(),
  vendorName: z.string().nullable().optional(),
});

// --- Delivery ---
export const queryDeliverySchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  customerId: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  orderId: z.string().nullable().optional(),
  deliveryDateFrom: z.string().nullable().optional(),
  deliveryDateTo: z.string().nullable().optional(),
  // Added for inline filters
  deliveryId: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  productName: z.string().nullable().optional(),
  // Tab "Giao hàng" của sản phẩm: chỉ phiếu có dòng thuộc sản phẩm này (trước đây bị bỏ qua → hiện mọi phiếu)
  productId: z.string().nullable().optional(),
  deliveryType: z.union([z.string(), z.array(z.string())]).nullable().optional(),
});

// --- Technical ---
export const queryTechnicalSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  type: z.string().nullable().optional(),
  plateType: z.string().nullable().optional(),
  logDateFrom: z.string().nullable().optional(),
  logDateTo: z.string().nullable().optional(),
  createdDateFrom: z.string().nullable().optional(),
  createdDateTo: z.string().nullable().optional(),
  // Added for inline filters
  specId: z.string().nullable().optional(),
  productName: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  moldId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
});

export const createTechnicalSpecSchema = z.object({
  customerId: z.string().nullable().optional(),
  productId: z.string().nullable().optional(), // sản phẩm của tiêu chuẩn (trước bị bỏ nên TSKT không gắn sản phẩm)
  productName: z.string().min(1),
  // Enum lấy từ DB (SpecStatus) -- trước đây z.string() nên giá trị sai gây lỗi Prisma thay vì 400
  status: SpecStatusEnumSchema.optional().default('PENDING'),
  designStatus: z.enum(['PENDING', 'DESIGNING', 'REVIEWING', 'APPROVED', 'REJECTED']).nullable().optional(),
  designNotes: z.string().nullable().optional(),
  version: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  materialId: z.string().nullable().optional(),
  packingSpec: z.string().nullable().optional(),
  process: z.string().nullable().optional(),
  layoutId: z.string().nullable().optional(),
  dieCutId: z.string().nullable().optional(),
  foilDieId: z.string().nullable().optional(),
  embossDieId: z.string().nullable().optional(),
  screenId: z.string().nullable().optional(),
  designFile: z.string().nullable().optional(),
  specDetails: z.object({
    printSize: z.string().nullable().optional(),
    ups: z.string().nullable().optional(),
    grainDirection: z.string().nullable().optional(),
    finishedSizeLength: z.string().nullable().optional(),
    finishedSizeWidth: z.string().nullable().optional(),
    finishedSizeHeight: z.string().nullable().optional(),
    steps: z.record(z.any()).nullable().optional(),
  }).optional(),
});

export const updateTechnicalSpecSchema = z.object({
  id: z.string().min(1),
  data: createTechnicalSpecSchema.partial()
});

export const createMoldSchema = z.object({
  name: z.string().trim().min(1, 'Tên khuôn là bắt buộc'),
  productName: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  productId: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
  // Mã cũ của danh mục "Loại khuôn" (FOIL / EMBOSS / DEBOSS) quy về enum ToolingType (phase294 đã đổi mã danh mục)
  type: z.preprocess(
    (v) => ({ FOIL: 'FOIL_STAMP', EMBOSS: 'EMBOSS_DIE', DEBOSS: 'DEBOSS_DIE' } as Record<string, string>)[String(v)] ?? v,
    z.enum(['PLATE', 'DIE_CUT', 'FOIL_STAMP', 'EMBOSS_DIE', 'DEBOSS_DIE', 'SPOT_UV', 'SILK_SCREEN']).nullable().optional(),
  ),
  location: z.string().nullable().optional(),
  storageZone: z.string().nullable().optional(),
  storageRack: z.string().nullable().optional(),
  storageShelf: z.string().nullable().optional(),
  storageBin: z.string().nullable().optional(),
  machine: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  images: z.any().nullable().optional(),
  notes: z.string().nullable().optional(),
  status: z.string().optional().default('PENDING'),
  quantity: z.number().int().min(1, 'Số lượng phải lớn hơn hoặc bằng 1').optional().default(1),
  attachments: z.any().nullable().optional(),
  usageCount: z.number().int().min(0, 'Số lần sử dụng phải lớn hơn hoặc bằng 0').optional().default(0),
  maxImpressions: looseNumber(z.number().int().min(1, 'Định mức dập tối đa phải lớn hơn 0')),
  maintenanceInterval: looseNumber(z.number().int().min(1, 'Chu kỳ bảo dưỡng phải lớn hơn 0')),
  lastMaintenanceImpressions: looseNumber(z.number().int().min(0, 'Mốc bảo dưỡng phải lớn hơn hoặc bằng 0')),
  version: z.number().int().optional().default(1),
  expectedDate: z.union([z.string(), z.date()]).nullable().optional(),
  dieLength: looseNumber(z.number().positive('Chiều dài khuôn phải lớn hơn 0')),
  dieWidth: looseNumber(z.number().positive('Chiều rộng khuôn phải lớn hơn 0')),
  productLength: looseNumber(z.number().positive('Chiều dài sản phẩm phải lớn hơn 0')),
  productWidth: looseNumber(z.number().positive('Chiều rộng sản phẩm phải lớn hơn 0')),
  ups: looseNumber(z.number().int().min(1, 'Số UPS phải lớn hơn hoặc bằng 1')),
  isShared: z.union([z.string(), z.boolean()]).nullable().optional(),
  discardReason: z.string().nullable().optional(),
  replacesId: z.string().nullable().optional(),
});

export const updateMoldSchema = z.object({
  id: z.string().min(1),
  data: createMoldSchema.partial()
});

export const discardAndReplaceMoldSchema = z.object({
  id: z.string().min(1),
  reason: z.string().min(1, 'Lý do hủy và thay thế là bắt buộc'),
  notes: z.string().optional().nullable(),
});

export const performMoldMaintenanceSchema = z.object({
  dieCutId: z.string().min(1),
  notes: z.string().optional().nullable(),
  technicianName: z.string().optional().nullable(),
  attachments: z.array(z.string()).optional(),
  maintenanceType: z.enum(['ROUTINE', 'REPAIR']).optional().default('ROUTINE'),
  newVersion: z.number().int().positive().optional().nullable(),
  changeReason: z.string().optional().nullable(),
});

export const recordMoldUsageSchema = z.object({
  dieCutId: z.string().min(1),
  productionOrderId: z.string().optional().nullable(),
  poCode: z.string().optional().nullable(),
  machineId: z.string().optional().nullable(),
  machineName: z.string().optional().nullable(),
  operatorId: z.string().optional().nullable(),
  operatorName: z.string().optional().nullable(),
  impressions: z.number().int().min(1, 'Số lượt dập phải lớn hơn 0'),
  notes: z.string().optional().nullable(),
  startedAt: z.union([z.string(), z.date()]).optional(),
  completedAt: z.union([z.string(), z.date()]).optional(),
});

export const recordPlateUsageSchema = z.object({
  plateId: z.string().min(1),
  productionOrderId: z.string().optional().nullable(),
  poCode: z.string().optional().nullable(),
  machineId: z.string().optional().nullable(),
  machineName: z.string().optional().nullable(),
  operatorId: z.string().optional().nullable(),
  operatorName: z.string().optional().nullable(),
  impressions: z.number().int().min(1, 'Số lượt in phải lớn hơn 0'),
  notes: z.string().optional().nullable(),
  startedAt: z.union([z.string(), z.date()]).optional(),
  completedAt: z.union([z.string(), z.date()]).optional(),
});

export const createPlateSchema = z.object({
  name: z.string().min(1),
  productId: z.string().nullable().optional(), // sản phẩm của bộ kẽm (trước bị bỏ khi lưu)
  location: z.string().nullable().optional(), // vị trí nhập tay; trống thì ghép từ khu / kệ / tầng
  productName: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
  colors: z.array(z.string()).nullable().optional(),
  designFileUrl: z.string().nullable().optional(),
  ctpFileUrl: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  attachments: z.any().nullable().optional(),
  quantity: z.number().int().optional().default(1),
  // Enum lấy từ DB (ToolingStatus). Bỏ default 'ACTIVE' cũ vì không thuộc enum -> để DB áp default.
  // Mã cũ của danh mục trạng thái kẽm (READY / MOUNTED / STORED / DISPOSED) quy về enum ToolingStatus (phase294)
  status: z.preprocess(
    (v) => ({ READY: 'AVAILABLE', STORED: 'AVAILABLE', MOUNTED: 'IN_USE', DISPOSED: 'DISCARDED' } as Record<string, string>)[String(v)] ?? v,
    ToolingStatusEnumSchema.optional(),
  ),
  version: z.number().int().optional().default(1),
  expectedDate: z.union([z.string(), z.date()]).nullable().optional(),
  usageCount: z.number().int().optional().default(0),
  maxImpressions: z.union([z.string(), z.number()]).nullable().optional(),
  storageZone: z.string().nullable().optional(),
  storageRack: z.string().nullable().optional(),
  storageShelf: z.string().nullable().optional(),
  // Phase 222 fields
  proofStatus: z.string().optional().nullable(),
  activeMachineId: z.string().optional().nullable(),
  activeMachineName: z.string().optional().nullable(),
  damageReason: z.string().optional().nullable(),
  damagedColors: z.array(z.string()).optional(),
  remakeCount: z.number().int().optional().default(0),
});

export const updatePlateSchema = z.object({
  id: z.string().min(1),
  data: createPlateSchema.partial()
});

export const approvePlateProofSchema = z.object({
  id: z.string().min(1),
  status: z.enum(['APPROVED', 'REJECTED', 'BYPASSED']),
  approver: z.string().min(1, 'Vui lòng nhập tên người duyệt'),
  deltaE: z.number().optional().nullable(),
  targetDensity: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  sampleUrl: z.string().optional().nullable(),
});

export const mountPlateToMachineSchema = z.object({
  id: z.string().min(1),
  machineId: z.string().min(1, 'Vui lòng chọn máy in'),
  machineName: z.string().min(1, 'Vui lòng chọn máy in'),
  operatorName: z.string().min(1, 'Vui lòng nhập tên thợ máy nhận kẽm'),
  shift: z.string().optional().nullable(),
  colors: z.array(z.string()).optional().default([]),
  rackPosition: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export const reportPlateDamagedAndRemakeSchema = z.object({
  id: z.string().min(1),
  damagedColors: z.array(z.string()).min(1, 'Vui lòng chọn ít nhất 1 màu kẽm bị hỏng'),
  damageReason: z.string().min(3, 'Vui lòng nhập lý do hỏng kẽm'),
  damageNotes: z.string().optional().nullable(),
  remakeQty: z.number().int().min(1, 'Số lượng kẽm ghi bù tối thiểu là 1').default(1),
  urgency: z.enum(['NORMAL', 'URGENT']).default('NORMAL'),
});

// --- Comment ---
export const getCommentsSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  cursor: z.string().optional(),
});

export const createCommentSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  content: z.string().min(1),
});

export const updateCommentSchema = z.object({
  id: z.string().min(1),
  content: z.string().min(1),
});

// --- Dashboard ---
export const dashboardQuerySchema = z.object({
  period: z.string().nullable().optional(),
}).optional();

// --- Notification ---
export const queryNotificationSchema = z.object({
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(1000).optional(),
});

// --- Transaction ---
export const queryTransactionSchema = paginationSchema.extend({
  type: z.string().nullable().optional(),
  materialId: z.string().nullable().optional(),
});

// --- Config ---
export const configKeySchema = z.object({
  key: z.string().min(1),
});

export const configUpdateSchema = z.object({
  key: z.string().min(1),
  value: z.unknown(),
});

// --- Material Planning ---
export const queryMaterialPlanningSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  category: z.string().nullable().optional(),
  materialName: z.string().nullable().optional(),
  materialCode: z.string().nullable().optional(),
});

export const createMaterialPlanSchema = z.object({
  productionOrderId: z.string().min(1, "Lệnh sản xuất không được để trống"),
  plannedMaterialId: z.string().min(1, "Vật tư kế hoạch không được để trống"),
  originalMaterialId: z.string().optional().nullable(),
  componentId: z.string().optional().nullable(),
  printLayoutId: z.string().optional().nullable(),
  routingStepId: z.string().optional().nullable(),
  isPrint: z.boolean().optional().default(true),
  plannedQuantity: z.number().nonnegative().optional().default(0),
  qtyRequired: z.number().nonnegative().default(0),
  qtyAvailable: z.number().nonnegative().default(0),
  qtyToOrder: z.number().nonnegative().default(0),
  setupSheets: z.number().int().nonnegative().default(0),
  wastePercentage: z.number().nonnegative().default(0),
  unit: z.string().default('sheet'),
  status: z.enum(['PENDING', 'PARTIAL', 'RECEIVED', 'ORDERED', 'ALLOCATED']).default('PENDING'),
  suppliedSize: z.string().optional().nullable(),
  splitReason: z.string().optional().nullable(),
  parentId: z.string().optional().nullable(),
  expectedDate: z.coerce.date().optional().nullable(),
});

export const updateMaterialPlanSchema = createMaterialPlanSchema.partial();

// --- Attachment ---
export const getAttachmentsSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
});

export const createAttachmentSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  fileName: z.string().min(1),
  fileUrl: z.string().min(1),
  fileSize: z.number().int().optional(),
  mimeType: z.string().optional(),
});

// --- Activity Log ---
export const getActivityLogsSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
  cursor: z.string().optional(),
  limit: z.number().optional(),
});

export const getAuditInfoSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
});

export const listActivityLogsSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(10000).default(20),
  entityType: z.union([z.string(), z.array(z.string())]).optional(),
  action: z.union([z.string(), z.array(z.string())]).optional(),
  userId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  search: z.string().optional(),
  // Bộ lọc theo cột
  userName: z.string().optional(),
  entityId: z.string().optional(),
  details: z.string().optional(),
  createdAtText: z.string().optional(),
  // Sắp xếp
  sortBy: z.enum(['createdAt', 'userName', 'entityType', 'entityId', 'action']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

// --- Finance / Receipts ---
export const queryReceiptSchema = paginationSchema.extend({
  type: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  customerId: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  receiptId: z.string().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  receiptDateFrom: z.string().nullable().optional(),
  receiptDateTo: z.string().nullable().optional(),
});


export const createOrderDtoSchema = z.object({
  orderId: z.string().optional().nullable(),
  customerId: z.string().min(1),
  customerName: z.string().optional().nullable(),
  salesUserId: z.string().optional().nullable(),
  /** Lưu nháp / Lưu (plan-draft-and-save-buttons-v1) */
  saveMode: z.enum(['DRAFT', 'SUBMIT']).optional(),
  contactPerson: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  orderDate: z.union([z.string(), z.date()]).optional().nullable(),
  deliveryDeadline: z.union([z.string(), z.date()]).optional().nullable(),
  approvedDate: z.union([z.string(), z.date()]).optional().nullable(),
  vatRate: z.number().optional().nullable(),
  shippingFee: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
  exchangeRate: z.number().optional().nullable(),
  deposit: z.union([z.string(), z.number()]).optional().nullable(),
  paymentTerm: z.string().optional().nullable(),
  deliveryAddress: z.string().optional().nullable(),
  deliveryMethod: z.string().optional().nullable(),
  deliveryNotes: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).optional().nullable(),
  notes: z.string().optional().nullable(),
  orderType: z.string().optional().nullable(),
  sampleType: z.string().optional().nullable(),
  quotationId: z.string().optional().nullable(),
  orderDetails: z.array(z.object({
    id: z.string().optional().nullable(),
    productCode: z.string().optional().nullable(),
    productName: z.string().optional().nullable(),
    quantity: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : 0), z.number()),
    unitPrice: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : 0), z.number()),
    unit: z.string().optional().nullable(),
    specs: z.string().optional().nullable(),
    process: z.string().optional().nullable(),
    materialId: z.string().optional().nullable(),
    purchasePrice: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    priceVND: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    priceUSD: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    notes: z.string().optional().nullable(),
    poReference: z.string().max(50, "Tối đa 50 ký tự").optional().nullable(),
    soReference: z.string().max(50, "Tối đa 50 ký tự").optional().nullable(),
    customerPo: z.string().optional().nullable(),
    allowancePercent: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    stockQty: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    lsxQty: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    deliveryDeadline: z.union([z.string(), z.date()]).optional().nullable(),
    deliveryDate: z.union([z.string(), z.date()]).optional().nullable(),
    status: z.string().optional().nullable(),
    prepressCode: z.string().optional().nullable(),
    prepressPaperSize: z.string().optional().nullable(),
    prepressUps: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    prepressPrintSheets: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    prepressNotes: z.string().optional().nullable(),
    quotationDetailId: z.string().optional().nullable(),
    remainingPlanQty: z.preprocess((val) => (val !== null && val !== undefined && val !== "" ? Number(val) : null), z.number().optional().nullable()),
    isComposite: z.boolean().optional().nullable(),
    components: z.array(z.any()).optional().nullable(),
  })).optional().nullable(),
});

export const updateOrderDtoSchema = z.object({
  id: z.string().min(1),
  data: createOrderDtoSchema.partial(),
});

export const createReceiptDtoSchema = z.object({
  receiptId: z.string().optional().nullable(),
  /** Lưu nháp / Lưu (plan-draft-and-save-buttons-v1) */
  saveMode: z.enum(['DRAFT', 'SUBMIT']).optional(),
  type: z.string().optional().nullable(),
  categoryId: z.string().optional().nullable(),
  sourceType: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  amount: z.number().optional().nullable(),
  receiptDate: z.union([z.string(), z.date()]).optional().nullable(),
  paymentMethod: z.string().optional().nullable(),
  referenceNumber: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
  status: z.string().optional().nullable(),
});

export const updateReceiptDtoSchema = z.object({
  id: z.string().min(1),
  data: createReceiptDtoSchema.partial(),
});

// --- Quality ---
export const createQCCheckSchema = z.object({
  checkId: z.string().optional().nullable(),
  orderId: z.string().optional().nullable(),
  productName: z.string().optional().nullable(),
  checkTime: z.union([z.string(), z.date()]).optional().nullable(),
  inspectorId: z.string().min(1, "Người kiểm tra không được để trống"),
  status: z.enum(['PASS', 'FAIL', 'PENDING']),
  checklist: z.any().optional().nullable(),
  notes: z.string().optional().nullable()
});

export const updateQCCheckSchema = z.object({
  id: z.string().min(1),
  data: createQCCheckSchema.partial()
});

export const createNcrSchema = z.object({
  ncrId: z.string().optional().nullable(),
  source: z.enum(['INTERNAL', 'CUSTOMER', 'SUPPLIER']),
  sourceType: z.string().optional().nullable(),
  relatedId: z.string().optional().nullable(),
  relatedType: z.string().optional().nullable(),
  qcMaterialId: z.string().optional().nullable(),
  qcProcessId: z.string().optional().nullable(),
  qcFinalId: z.string().optional().nullable(),
  productionOrderId: z.string().optional().nullable(),
  routingStepId: z.string().optional().nullable(),
  processStage: z.string().optional().nullable(),
  machineId: z.string().optional().nullable(),
  materialId: z.string().optional().nullable(),
  vendorId: z.string().optional().nullable(),
  productId: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  batchNumber: z.string().optional().nullable(),
  lotSize: z.number().optional().nullable(),
  checkedQty: z.number().optional().nullable(),
  sampleSize: z.number().optional().nullable(),
  okQty: z.number().optional().nullable(),
  defectQty: z.number().optional().nullable(),
  unit: z.string().optional().nullable(),
  defectDetails: z.any().optional().nullable(),
  costBreakdown: z.any().optional().nullable(),
  resolutionResult: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
  defectCategory6M: z.string().optional().nullable(),
  disposition: z.string().optional().nullable(),
  containmentAction: z.string().optional().nullable(),
  actionPlan: z.string().optional().nullable(),
  correctiveAction: z.string().optional().nullable(), // D5, lưu vào actionPlan
  preventiveAction: z.string().optional().nullable(), // D7
  issueSummary: z.string().min(1, "Mô tả lỗi không được để trống"),
  rootCause: z.string().optional().nullable(),
  whyAnalysis: z.any().optional().nullable(),
  failedCriteria: z.any().optional().nullable(),
  severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
  status: z.enum(['OPEN', 'INVESTIGATING', 'CORRECTIVE_ACTION', 'VERIFICATION', 'PREVENTIVE_ACTION', 'CLOSED', 'CANCELLED']),
  responsibleDept: z.string().optional().nullable(),
  costImpact: z.number().optional().nullable(),
  ownerId: z.string().optional().nullable(),
  capaPlan: z.string().optional().nullable(),
});

export const updateNcrSchema = z.object({
  id: z.string().min(1),
  data: createNcrSchema.partial()
});

// --- Warehouse Inventory (NXT & Stocktake) ---
export const createInventorySnapshotSchema = z.object({
  periodMonth: z.number().int().min(1).max(12),
  periodYear: z.number().int().min(2000),
});

export const queryStockCardSchema = z.object({
  itemId: z.string().min(1),
  // Nhóm kho của sổ kho: vật tư / thành phẩm / kỹ thuật / bán thành phẩm / phế liệu (warehouse-domain)
  itemType: z.enum(['MATERIAL', 'FG', 'PRODUCT', 'TOOLING', 'TECH', 'WIP', 'SCRAP']),
  warehouseId: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional()
});

export const queryNxtReportSchema = z.object({
  periodMonth: z.number().int().min(1).max(12),
  periodYear: z.number().int().min(2000),
  itemType: z.enum(['MATERIAL', 'FG', 'TOOLING', 'TECH', 'ALL']).optional().default('ALL'),
  showZeroBalances: z.boolean().optional().default(false),
});

export const inventoryCheckLineSchema = z.object({
  itemType: z.string(),
  itemId: z.string(),
  itemCode: z.string().optional().nullable(),
  itemName: z.string().optional().nullable(),
  unit: z.string().optional().nullable(),
  lotNumber: z.string().optional().nullable(),
  locationId: z.string().optional().nullable(),
  systemQty: z.number(),
  actualQty: z.number(),
  diffQty: z.number(),
  reason: z.string().optional().nullable(),
});

export const createInventoryCheckSchema = z.object({
  warehouseId: z.string().min(1, 'Bắt buộc chọn kho kiểm kê').optional().nullable(),
  checkDate: z.string().or(z.date()),
  type: z.enum(['MATERIAL', 'FG', 'TOOLING', 'TECH', 'WIP', 'SCRAP', 'ALL']),
  notes: z.string().optional().nullable(),
  lines: z.array(inventoryCheckLineSchema).optional(),
});

export const updateInventoryCheckSchema = z.object({
  id: z.string().min(1),
  data: createInventoryCheckSchema.partial().extend({
    status: z.enum(['DRAFT', 'COMPLETED', 'CANCELLED', 'APPROVED']).optional(),
    lines: z.array(inventoryCheckLineSchema).optional(),
  })
});

// ============================================
// MES SMART FACTORY MODULE (PHASE 6)
// ============================================

export const createProductionTaskSchema = z.object({
  productionOrderId: z.string().min(1),
  processType: z.string().min(1),
  machineId: z.string().optional().nullable(),
  priority: z.number().int().optional().default(0),
  plannedStartDate: z.string().or(z.date()).optional().nullable(),
  plannedEndDate: z.string().or(z.date()).optional().nullable(),
  layoutUps: z.number().int().min(1).optional().default(1),
  plannedQty: z.number().int().min(1),
  materialRef: z.string().optional().nullable(),
  plateRef: z.string().optional().nullable(),
  outsourceRef: z.string().optional().nullable(),
});

export const updateProductionTaskSchema = z.object({
  id: z.string().min(1),
  data: createProductionTaskSchema.partial().extend({
    status: z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'DELAYED']).optional(),
  })
});

export const createProductionSessionSchema = z.object({
  taskId: z.string().min(1),
  machineId: z.string().min(1),
  operatorId: z.string().min(1),
  qtyOk: z.number().int().min(0).optional().default(0),
  qtyNg: z.number().int().min(0).optional().default(0),
  defectReason: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export const updateProductionSessionSchema = z.object({
  id: z.string().min(1),
  data: z.object({
    endTime: z.string().or(z.date()).optional().nullable(),
    qtyOk: z.number().int().min(0).optional(),
    qtyNg: z.number().int().min(0).optional(),
    defectReason: z.string().optional().nullable(),
    notes: z.string().optional().nullable(),
    qcFirstPieceApproved: z.boolean().optional(),
    qcInProcessNotes: z.string().optional().nullable(),
    qcFinalVerified: z.boolean().optional(),
    qcVerifierId: z.string().optional().nullable(),
  })
});

export const updateMachineStateSchema = z.object({
  machineId: z.string().min(1),
  status: z.enum(['IDLE', 'RUNNING', 'MAINTENANCE', 'DOWN']),
  currentTaskId: z.string().optional().nullable(),
  operatorId: z.string().optional().nullable(),
});

// ==========================================
// QC PROCESS, QC FINAL & QC MATERIAL SCHEMAS
// ==========================================

export const createQCMaterialSchema = z.object({
  materialId: z.string().min(1, "Bắt buộc chọn vật tư"),
  materialCategory: z.string().optional().nullable(),
  vendorId: z.string().optional().nullable(),
  batchNumber: z.string().optional().nullable(),
  purchaseOrderId: z.string().optional().nullable(),
  receiptDocId: z.string().optional().nullable(),
  receiptDocNo: z.string().optional().nullable(),
  supplierName: z.string().optional().nullable(),
  templateId: z.string().optional().nullable(),
  qtyReceived: z.number().optional().nullable(),
  qtyInspected: z.number().optional().nullable(),
  qtyAccepted: z.number().optional().nullable(),
  qtyRejected: z.number().optional().nullable(),
  lotSize: z.number().int().optional().nullable(),
  sampleSize: z.number().int().optional().nullable(),
  okSampleQty: z.number().int().optional().nullable(),
  ngSampleQty: z.number().int().optional().nullable(),
  attachments: z.unknown().optional().nullable(),
  aqlLevel: z.string().optional().nullable(),
  aqlMajor: z.number().optional().nullable(),
  aqlMinor: z.number().optional().nullable(),
  acMajor: z.number().optional().nullable(),
  reMajor: z.number().optional().nullable(),
  // Phase 290: cách kiểm (AQL | FULL), Ac/Re lỗi nhẹ, lý do nhân nhượng
  inspectionMode: z.enum(['AQL', 'FULL']).optional().nullable(),
  acMinor: z.number().int().min(0).optional().nullable(),
  reMinor: z.number().int().min(1).optional().nullable(),
  overrideReason: z.string().max(2000).optional().nullable(),
  criticalDefects: z.number().int().min(0).default(0).optional(),
  majorDefects: z.number().int().min(0).default(0).optional(),
  minorDefects: z.number().int().min(0).default(0).optional(),
  moisture: z.number().optional().nullable(),
  grammage: z.number().optional().nullable(),
  caliper: z.number().optional().nullable(),
  grainDirection: z.string().optional().nullable(),
  dyneLevel: z.number().optional().nullable(),
  viscosity: z.number().optional().nullable(),
  deltaE: z.number().optional().nullable(),
  coaVerified: z.boolean().optional().nullable(),
  checkTime: z.coerce.date().optional(),
  inspectorId: z.string().min(1, "Bắt buộc chọn người kiểm tra"),
  status: z.enum(['PENDING', 'INSPECTING', 'PASSED', 'WARNING', 'FAILED', 'REWORK', 'RE_INSPECT', 'PASS', 'FAIL']),
  checklist: z.unknown().optional().nullable(),
  technicalParams: z.unknown().optional().nullable(),
  defectDetails: z.unknown().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export const updateQCMaterialSchema = z.object({
  id: z.string().min(1),
  data: createQCMaterialSchema.partial()
});

export const createQCProcessSchema = z.object({
  productionOrderId: z.string().min(1, "Bắt buộc chọn Lệnh sản xuất"),
  componentId: z.string().optional().nullable(),
  routingStepId: z.string().optional().nullable(),
  stageProcessId: z.string().optional().nullable(),
  processStage: z.string().optional().nullable(),
  processStep: z.string().min(1, "Bắt buộc nhập công đoạn"),
  machineId: z.string().optional().nullable(),
  productId: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  templateId: z.string().optional().nullable(),
  checkType: z.enum(['FIRST_PIECE', 'ROUTINE', 'PATROL', 'LAST_PIECE']).default('FIRST_PIECE').optional(),
  reportStage: z.string().optional().nullable(),
  setupResult: z.string().optional().nullable(),
  setupCheckedAt: z.union([z.string(), z.date()]).optional().nullable(),
  setupSampleQty: z.number().int().min(0).optional().nullable(),
  setupDefectQty: z.number().int().min(0).optional().nullable(),
  setupNotes: z.string().optional().nullable(),
  patrolLogs: z.unknown().optional().nullable(),
  defectDetails: z.unknown().optional().nullable(),
  qtyPassed: z.number().int().min(0).optional().nullable(),
  attachments: z.unknown().optional().nullable(),
  sampleFrequency: z.string().optional().nullable(),
  patrolRound: z.number().int().min(1).default(1).optional().nullable(),
  shift: z.string().optional().nullable(),
  operatorName: z.string().optional().nullable(),
  lotSize: z.number().int().min(0).optional().nullable(),
  aqlLevel: z.string().optional().nullable(),
  qtySample: z.number().int().min(1).default(5).optional(),
  qtyDefect: z.number().int().min(0).default(0).optional(),
  criticalDefects: z.number().int().min(0).default(0).optional(),
  majorDefects: z.number().int().min(0).default(0).optional(),
  minorDefects: z.number().int().min(0).default(0).optional(),
  checkTime: z.coerce.date().optional(),
  inspectorId: z.string().min(1, "Bắt buộc chọn người kiểm tra"),
  status: z.enum(['PENDING', 'INSPECTING', 'PASSED', 'WARNING', 'FAILED', 'REWORK', 'RE_INSPECT']),
  checklist: z.unknown().optional().nullable(),
  technicalParams: z.unknown().optional().nullable(),
  notes: z.string().optional().nullable(),
  // Phase 291: IPQC đợt 3 — 4M, ký mẫu, cho sản xuất, lấy mẫu theo tổng cỡ lô, nhân nhượng
  fourMChecklist: z.unknown().optional().nullable(),
  referenceSampleNo: z.string().max(200).optional().nullable(),
  sampleRef: z.string().max(200).optional().nullable(),
  /** Gửi true / thời điểm để ký mẫu (máy chủ ghi giờ + người ký); null để bỏ ký (chỉ khi chưa cho sản xuất) */
  sampleApprovedAt: z.union([z.boolean(), z.string(), z.date()]).optional().nullable(),
  sampleApprovedBy: z.string().optional().nullable(),
  sampleShiftLead: z.string().max(200).optional().nullable(),
  /** Gửi true / thời điểm để cho sản xuất (bắt buộc đã ký mẫu) */
  releasedAt: z.union([z.boolean(), z.string(), z.date()]).optional().nullable(),
  inspectionMode: z.enum(['AQL', 'FULL']).optional().nullable(),
  aqlMajor: z.number().min(0).optional().nullable(),
  aqlMinor: z.number().min(0).optional().nullable(),
  acMajor: z.number().int().min(0).optional().nullable(),
  reMajor: z.number().int().min(1).optional().nullable(),
  acMinor: z.number().int().min(0).optional().nullable(),
  reMinor: z.number().int().min(1).optional().nullable(),
  overrideReason: z.string().max(2000).optional().nullable()
});

export const updateQCProcessSchema = z.object({
  id: z.string().min(1),
  data: createQCProcessSchema.partial()
});

const createQCFinalSchemaBase = z.object({
  // Lệnh SX, hoặc đơn gia công 100% + dòng đơn hàng khi hàng gia công trọn không có lệnh (04/11/2026)
  productionOrderId: z.string().optional().nullable(),
  outsourcePoId: z.string().optional().nullable(),
  orderDetailId: z.string().optional().nullable(),
  orderId: z.string().optional().nullable(),
  productId: z.string().optional().nullable(),
  productTypeId: z.string().optional().nullable(),
  customerId: z.string().optional().nullable(),
  templateId: z.string().optional().nullable(),
  productName: z.string().optional().nullable(),
  checkTime: z.coerce.date().optional(),
  inspectorId: z.string().min(1, "Bắt buộc chọn người kiểm tra"),
  status: z.enum(['PENDING', 'INSPECTING', 'PASSED', 'WARNING', 'FAILED', 'REWORK', 'RE_INSPECT']),
  qtyChecked: z.number().int().min(1, "Số lượng kiểm tra phải lớn hơn 0"),
  qtyPassed: z.number().int().min(0, "Số lượng đạt không được âm"),
  qtyFailed: z.number().int().min(0, "Số lượng lỗi không được âm"),
  aqlLevel: z.string().default('II').optional(),
  aqlStandard: z.preprocess((val) => {
    if (val === undefined || val === null || val === '') return 2.5;
    if (typeof val === 'number') return isNaN(val) ? 2.5 : val;
    if (typeof val === 'string') {
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 2.5 : parsed;
    }
    return 2.5;
  }, z.number().default(2.5)).optional(),
  lotSize: z.number().int().optional().nullable(),
  sampleSize: z.number().int().optional().nullable(),
  acValue: z.number().int().default(0).optional().nullable(),
  reValue: z.number().int().default(1).optional().nullable(),
  // Phase 290: cách kiểm (AQL | FULL), AQL + Ac/Re lỗi nhẹ, mẫu chuẩn, lý do nhân nhượng
  inspectionMode: z.enum(['AQL', 'FULL']).optional().nullable(),
  aqlMinor: z.number().min(0).optional().nullable(),
  acMinor: z.number().int().min(0).optional().nullable(),
  reMinor: z.number().int().min(1).optional().nullable(),
  referenceSample: z.string().max(500).optional().nullable(),
  overrideReason: z.string().max(2000).optional().nullable(),
  packagingCheck: z.boolean().default(true).optional(),
  actualLength: z.number().optional().nullable(),
  actualWidth: z.number().optional().nullable(),
  actualHeight: z.number().optional().nullable(),
  moisture: z.number().optional().nullable(),
  barcodeGrade: z.string().optional().nullable(),
  dropTestResult: z.string().optional().nullable(),
  fittingTestResult: z.string().optional().nullable(),
  checklist: z.unknown().optional().nullable(),
  technicalParams: z.unknown().optional().nullable(),
  notes: z.string().optional().nullable(),

  // Số lô, kho nhận, quy cách thùng / pallet, nhập kho: thuộc Phiếu đóng gói (packingSlips.pack), không nhận ở phiếu KCS

  // Phase 86: Defect Breakdown & Attachments
  okSampleQty: z.number().int().min(0).optional().nullable(),
  ngSampleQty: z.number().int().min(0).optional().nullable(),
  criticalDefects: z.number().int().min(0).default(0).optional(),
  majorDefects: z.number().int().min(0).default(0).optional(),
  minorDefects: z.number().int().min(0).default(0).optional(),
  defectDetails: z.unknown().optional().nullable(),
  coaNo: z.string().optional().nullable(),
  coaIssuedAt: z.union([z.string(), z.date()]).optional().nullable(),
  attachments: z.unknown().optional().nullable(),
});

export const createQCFinalSchema = createQCFinalSchemaBase.refine((data) => Boolean(data.productionOrderId || data.outsourcePoId), {
  message: "Bắt buộc chọn Lệnh sản xuất hoặc đơn gia công 100%",
  path: ["productionOrderId"],
}).refine(data => data.qtyPassed + data.qtyFailed === data.qtyChecked, {
  message: "Tổng số lượng Đạt và Lỗi phải bằng số lượng kiểm tra",
  path: ["qtyChecked"]
});

export const updateQCFinalSchema = z.object({
  id: z.string().min(1),
  data: createQCFinalSchemaBase.partial().refine(
    (data) => {
      // If none of the quantities are updated, it's valid
      if (data.qtyPassed === undefined && data.qtyFailed === undefined && data.qtyChecked === undefined) return true;
      return true;
    },
    { message: "Cần cập nhật đồng bộ các trường số lượng" }
  )
});

// ==========================================
// QC TEMPLATES SCHEMAS
// ==========================================

export const queryQcTemplateSchema = paginationSchema.extend({
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  type: z.string().nullable().optional(),
  scope: z.string().nullable().optional(),
  materialType: z.string().nullable().optional(),
  processStage: z.string().nullable().optional(),
  productTypeId: z.string().nullable().optional(),
  /** Danh sách quản lý: mỗi mã 1 dòng (bản đang áp dụng / mới nhất) + số phiên bản */
  latestOnly: z.boolean().optional(),
});

export const criteriaSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['QUANTITATIVE', 'QUALITATIVE', 'EVALUATION']),
  category: z.string().optional(),
  description: z.string().optional(),
  method: z.string().optional(),
  standard: z.string().optional(),
  minValue: z.number().optional().nullable(),
  maxValue: z.number().optional().nullable(),
  targetValue: z.number().optional().nullable(),
  unit: z.string().optional(),
  severity: z.enum(['CRITICAL', 'MAJOR', 'MINOR']).default('MAJOR').optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean().default(true),
  weight: z.number().default(1).optional(),
  // Khoá tuỳ chọn (không đổi khoá cũ): mức lỗi, dụng cụ, nguồn; tên ngắn; IPQC: giai đoạn + lặp lại khi chạy
  defectType: z.enum(['CRITICAL', 'MAJOR', 'MINOR']).optional(),
  toolUsed: z.string().optional().nullable(),
  verificationSource: z.enum(['SHOPFLOOR_MEASURED', 'SUPPLIER_COA', 'EXTERNAL_LAB']).optional(),
  shortName: z.string().max(80).optional().nullable(),
  stage: z.enum(['SETUP', 'PATROL', 'BOTH']).optional().nullable(),
  repeat: z.boolean().optional().nullable()
});

export const createQcTemplateSchema = z.object({
  code: z.string().min(1, "Mã template không được để trống"),
  name: z.string().min(1, "Tên template không được để trống"),
  description: z.string().optional().nullable(),
  scope: z.enum(['IQC', 'IPQC', 'FQC']).default('IPQC').optional(),
  type: z.string().optional().nullable(),
  machineType: z.preprocess(
    (v) => (v === '' ? null : v),
    MachineTypeEnumSchema.optional().nullable()
  ),
  materialType: z.string().optional().nullable(),
  processStage: z.string().optional().nullable(),
  productTypeId: z.string().optional().nullable(),
  productCategory: z.string().optional().nullable(),
  samplingRule: z.string().optional().nullable(),
  aqlCritical: z.number().optional().nullable(),
  aqlMajor: z.number().optional().nullable(),
  aqlMinor: z.number().optional().nullable(),
  criteria: z.array(criteriaSchema).min(1, "Template cần có ít nhất 1 tiêu chí"),
  status: z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']).default('ACTIVE'),
  // IPQC: tần suất kiểm tra lặp lại, số mẫu ký mẫu (lưu trong schema của mẫu)
  patrolFrequency: z.string().max(200).optional().nullable(),
  setupSampleQty: z.number().int().min(0).optional().nullable()
});

export const updateQcTemplateSchema = z.object({
  id: z.string().min(1),
  data: createQcTemplateSchema.partial()
});

export const publishQcTemplateSchema = z.object({
  id: z.string().min(1),
  notes: z.string().optional()
});


export const createLayoutSchema = z.object({
  layoutId: z.string().optional(),
  name: z.string().min(1),
  productName: z.string().nullable().optional(),
  sheetSize: z.string().nullable().optional(),
  ups: z.number().int().nullable().optional(),
  gripperMargin: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  fileUrl: z.string().nullable().optional(),
});

export const updateLayoutSchema = z.object({
  id: z.string().min(1),
  data: createLayoutSchema.partial()
});

export const createProcessSchema = z.object({
  stepId: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
  defaultMachineType: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  cmyk: z.boolean().default(false),
  pantone: z.boolean().default(false),
  setupCost: z.number().nullable().optional(),
  unitCost: z.number().nullable().optional(),
  speed: z.number().int().nullable().optional(),
  productionTime: z.number().int().nullable().optional(),
  wastePercent: z.number().nullable().optional(),
  minWasteQty: z.number().int().nullable().optional(),
  defaultSetupTime: z.number().int().nullable().optional(),
  defaultSpeed: z.number().int().nullable().optional(),
  defaultSetupWaste: z.number().int().nullable().optional(),
  defaultRunWastePct: z.number().nullable().optional(),
  defaultBatchCost: z.number().nullable().optional(),
  defaultUnitCost: z.number().nullable().optional(),
  sortOrder: z.number().int().optional(),
  processGroupId: z.string().nullable().optional(),
  requiresQc: z.boolean().optional(),
  requiresTooling: z.array(z.string()).optional(),
  specs: z.any().optional(),
});

export const updateProcessSchema = z.object({
  id: z.string().min(1),
  data: createProcessSchema.partial()
});

export const createCtpLogSchema = z.object({
  date: z.union([z.string(), z.date()]).optional(),
  machineId: z.string().optional(),
  plateId: z.string().optional(),
  quantity: z.number().int().optional(),
  status: z.string().optional(),
}).catchall(z.any());

export const updateCtpLogSchema = z.object({
  id: z.string().min(1),
  data: createCtpLogSchema.partial()
});

// PRICING CONFIG
export const createPricingConfigMachineSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  maxSheetWidth: z.number().min(0),
  maxSheetLength: z.number().min(0),
  baseBatchSize: z.number().int().min(1),
  cmykBatchCost: z.number().min(0),
  cmykRunOnCost: z.number().min(0),
  spotColorCostMultiplier: z.number().min(0).default(2.0),
  uvInkCostMultiplier: z.number().min(0).default(3.0),
  plateCost: z.number().min(0).default(0),
  makeReadyCost: z.number().min(0).default(0),
  washUpCostSpotColor: z.number().min(0).default(0),
  status: z.string().default('ACTIVE'),
});

export const updatePricingConfigMachineSchema = z.object({
  id: z.string().min(1),
  data: createPricingConfigMachineSchema.partial()
});

export const createPricingConfigProcessSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  type: z.string().min(1),
  minBatchCost: z.number().min(0),
  unitCost: z.number().min(0),
  unitType: z.string().min(1),
  moldPricingType: z.string().optional().default('FIXED'),
  baseMoldPrice: z.number().nullable().optional(),
  moldFormula: z.string().nullable().optional(),
  moldFixedCost: z.number().min(0).default(0),
  status: z.string().default('ACTIVE'),
});

export const updatePricingConfigProcessSchema = z.object({
  id: z.string().min(1),
  data: createPricingConfigProcessSchema.partial()
});

export const createPricingConfigWastageSchema = z.object({
  processType: z.string().min(1),
  minQty: z.number().int().min(0),
  maxQty: z.number().int().min(0),
  setupWasteSheets: z.number().int().min(0),
  runWastePercent: z.number().min(0),
});

export const updatePricingConfigWastageSchema = z.object({
  id: z.string().min(1),
  data: createPricingConfigWastageSchema.partial()
});

// --- Inventory Document ---
export const queryInventoryDocumentSchema = paginationSchema.extend({
  docType: z.string().nullable().optional(),
  itemType: z.string().nullable().optional(),
  status: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  warehouseId: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  productionOrderId: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});

export const inventoryDocumentLineSchema = z.object({
  id: z.string().optional(),
  itemType: z.string(),
  itemId: z.string(),
  itemCode: z.string().nullable().optional(),
  itemName: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  quantity: z.number().refine(val => val !== 0, { message: 'Số lượng không được bằng 0' }),
  unitPrice: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  lotNumber: z.string().nullable().optional(),
  expiryDate: z.string().or(z.date()).nullable().optional(),
  locationId: z.string().nullable().optional().transform(v => (v && typeof v === 'string' && v.trim() !== '' ? v.trim() : null)),
  notes: z.string().nullable().optional(),
});

const nullableFkString = z
  .string()
  .nullable()
  .optional()
  .transform(v => (v && typeof v === 'string' && v.trim() !== '' ? v.trim() : null));

export const createInventoryDocumentSchema = z.object({
  /** Lưu nháp / Lưu (plan-draft-and-save-buttons-v1) */
  saveMode: z.enum(['DRAFT', 'SUBMIT']).optional(),
  docType: z.enum(['RECEIPT', 'ISSUE', 'ADJUSTMENT', 'TRANSFER']),
  sourceWarehouseId: nullableFkString,
  targetWarehouseId: nullableFkString,
  vendorId: nullableFkString,
  customerId: nullableFkString,
  productionOrderId: nullableFkString,
  orderId: nullableFkString,
  deliveryId: nullableFkString,
  referenceDoc: z.string().nullable().optional(),
  driverName: z.string().nullable().optional(),
  vehiclePlate: z.string().nullable().optional(),
  expectedDate: z.union([z.string(), z.date()]).nullable().optional(),
  actualDate: z.union([z.string(), z.date()]).nullable().optional(),
  notes: z.string().nullable().optional(),
  lines: z.array(inventoryDocumentLineSchema).min(1),
});

export const updateInventoryDocumentSchema = z.object({
  id: z.string(),
  data: createInventoryDocumentSchema.partial(),
});

export const completeInventoryDocumentSchema = z.object({
  id: z.string(),
});

// --- Estimating & Multi-Machine Simulation ---
export const simulateMultiMachineSchema = z.object({
  orderQty: z.number().int().positive().default(7000),
  flatWidth: z.number().positive().default(210), // mm
  flatLength: z.number().positive().default(280), // mm
  paperType: z.string().default('Ivory'),
  gsm: z.number().positive().default(300),
  paperPriceKg: z.number().positive().default(28000),
  colors: z.number().int().min(1).max(8).default(4),
  spotColors: z.number().int().min(0).max(4).default(0),
  hasLamination: z.boolean().default(true),
  laminationType: z.enum(['BOPP_MATTE', 'BOPP_GLOSS', 'METALLIZED', 'NONE']).default('BOPP_MATTE'),
  hasFoil: z.boolean().default(false),
  foilAreaCm2: z.number().nonnegative().default(0),
  hasFlute: z.boolean().default(false),
  fluteType: z.enum(['E', 'B', 'F', 'NONE']).default('NONE'),
  diecutType: z.enum(['AUTO', 'MANUAL']).default('AUTO'),
  glueType: z.enum(['SIDE_SEAM', 'AUTO_LOCK_BOTTOM', 'SNAP_BOTTOM', 'FOUR_CORNER', 'MANUAL']).default('AUTO_LOCK_BOTTOM'),
  boxPreset: z.string().optional(),
  marginPercent: z.number().nonnegative().default(20),
  overheadPercent: z.number().nonnegative().default(10),
  baseThreshold: z.number().int().positive().default(2000), // 1000, 2000, 5000
});

export const boxPresetSchema = z.object({
  code: z.string(),
  name: z.string(),
  standard: z.enum(['ECMA', 'FEFCO', 'CATALOGUE']),
  length: z.number().positive(),
  width: z.number().positive(),
  height: z.number().positive(),
  paperThickness: z.number().positive().default(0.4),
});

export const simulateMultiTierPricingSchema = simulateMultiMachineSchema.extend({
  tierQuantities: z.array(z.number().int().positive()).default([1000, 3000, 5000, 7000, 10000, 20000, 50000, 100000]),
});

export const convertTradeoffToQuotationSchema = z.object({
  customerId: z.string().min(1),
  productName: z.string().default('Hộp Bao Bì Offset Theo Yêu Cầu'),
  orderQty: z.number().int().positive(),
  selectedMachineKey: z.enum(['SMALL', 'LARGE']).default('SMALL'),
  tradeoffParams: simulateMultiMachineSchema,
  notes: z.string().optional(),
  validUntil: z.string().optional(),
});

export const convertQuotationToOrderSchema = z.object({
  quotationId: z.string().min(1),
  deliveryDate: z.string().optional(),
  depositAmount: z.number().nonnegative().optional(),
  notes: z.string().optional(),
});

export const createProductionOrderFromEstimateSchema = z.object({
  orderDetailId: z.string().optional(),
  costEstimateId: z.string().min(1),
  orderId: z.string().optional(),
  plannedStartDate: z.string().optional(),
  deadline: z.string().optional(),
  notes: z.string().optional(),
});

export const issueMaterialsForProductionOrderSchema = z.object({
  productionOrderId: z.string().min(1),
  warehouseId: z.string().optional(),
  notes: z.string().optional(),
});

export const receiveFinishedGoodsFromProductionOrderSchema = z.object({
  productionOrderId: z.string().min(1),
  quantityOk: z.number().int().positive(),
  quantityNg: z.number().int().nonnegative().optional().default(0),
  warehouseId: z.string().optional(),
  notes: z.string().optional(),
});

export const createDeliveryFromProductionOrderSchema = z.object({
  productionOrderId: z.string().min(1),
  deliveryAddress: z.string().optional(),
  driverInfo: z.string().optional(),
  vehiclePlate: z.string().optional(),
  cartonCount: z.number().int().positive().optional(),
  pcsPerCarton: z.number().int().positive().optional(),
  notes: z.string().optional(),
});

/** Hình thức thu tiền: mã chuẩn của phân hệ Thu chi; nhận cả mã cũ CASH / BANK_TRANSFER / CONG_NO */
export const RECEIPT_PAYMENT_METHODS = ['TIEN_MAT', 'CHUYEN_KHOAN', 'CAN_TRU'] as const;
const LEGACY_PAYMENT_METHOD: Record<string, (typeof RECEIPT_PAYMENT_METHODS)[number]> = {
  CASH: 'TIEN_MAT',
  BANK_TRANSFER: 'CHUYEN_KHOAN',
  CONG_NO: 'CAN_TRU',
};
export const receiptPaymentMethodSchema = z
  .preprocess((v) => (typeof v === 'string' && LEGACY_PAYMENT_METHOD[v] ? LEGACY_PAYMENT_METHOD[v] : v), z.enum(RECEIPT_PAYMENT_METHODS))
  .default('CHUYEN_KHOAN');

export const createReceiptFromOrderSchema = z.object({
  orderId: z.string().min(1),
  amount: z.number().positive(),
  paymentMethod: receiptPaymentMethodSchema,
  referenceNo: z.string().optional(),
  notes: z.string().optional(),
});

export const createReceiptFromDeliverySchema = z.object({
  deliveryId: z.string().min(1),
  amount: z.number().positive(),
  paymentMethod: receiptPaymentMethodSchema,
  referenceNo: z.string().optional(),
  notes: z.string().optional(),
});

export const getARAgingReportSchema = z.object({
  customerId: z.string().optional(),
  asOfDate: z.string().optional(),
});

export const createPurchaseOrderFromProductionOrderSchema = z.object({
  productionOrderId: z.string().min(1),
  vendorId: z.string().min(1),
  expectedDate: z.string().optional(),
  unitPrice: z.number().positive().optional(),
  notes: z.string().optional(),
});

export const receiveMaterialsFromPurchaseOrderSchema = z.object({
  purchaseOrderId: z.string().min(1),
  warehouseId: z.string().optional(),
  notes: z.string().optional(),
});

export const createPaymentVoucherForVendorSchema = z.object({
  purchaseOrderId: z.string().min(1),
  amount: z.number().positive(),
  paymentMethod: z.enum(['CASH', 'BANK_TRANSFER']).default('BANK_TRANSFER'),
  referenceNo: z.string().optional(),
  notes: z.string().optional(),
});

export const wmsScanLookupSchema = z.object({
  code: z.string().min(1, 'Mã không được để trống'),
  warehouseId: z.string().optional(),
});

export const wmsQuickActionSchema = z.object({
  action: z.enum(['QUICK_RECEIPT', 'QUICK_ISSUE', 'QUICK_STOCKTAKE_COUNT']),
  itemType: z.enum(['MATERIAL', 'FG', 'TECH', 'TOOLING']),
  itemId: z.string().min(1),
  warehouseId: z.string().min(1),
  quantity: z.number().positive('Số lượng phải lớn hơn 0'),
  unitPrice: z.number().optional(),
  lotNumber: z.string().optional(),
  location: z.string().optional(),
  productionOrderId: z.string().optional(),
  notes: z.string().optional(),
});

// --- Calculation Schemas ---
// Schema chung để tính toán tổng tiền, VAT, grand total
export const calculateTotalsInputSchema = z.object({
  items: z.array(z.object({
    quantity: z.number(),
    unitPrice: z.number(),
  })),
  vatRate: z.number().optional().default(0),
  shippingFee: z.number().optional().default(0),
});

// --- Smart Import & Batch Staging Schemas (Phase 182) ---
export const inspectImportBatchItemSchema = z.object({
  productCode: z.string().optional().nullable(),
  productName: z.string().optional().nullable(),
  specs: z.string().optional().nullable(),
  unit: z.string().optional().nullable(),
  quantity: z.coerce.number().optional().nullable(),
  unitPrice: z.coerce.number().optional().nullable(),
  costPrice: z.coerce.number().optional().nullable(),
  poReference: z.string().optional().nullable(),
  soReference: z.string().optional().nullable(),
  customerPo: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

export const inspectImportBatchSchema = z.object({
  items: z.array(inspectImportBatchItemSchema),
  customerId: z.string().optional().nullable(),
  scope: z.enum(['ORDER', 'CATALOG']).default('ORDER'),
});

export const stagingItemSchema = z.object({
  id: z.string(),
  rowNumber: z.number(),
  matchStatus: z.enum([
    'MATCHED_EXACT',
    'MATCHED_NEEDS_REVIEW',
    'NEW_VALID',
    'NEW_NEEDS_ENRICHMENT',
    'CONFLICT',
  ]),
  statusReason: z.string().optional(),
  existingProductId: z.string().optional().nullable(),
  isNewProduct: z.boolean(),
  productCode: z.string(),
  isAutoGeneratedCode: z.boolean().default(false),
  productName: z.string(),
  customerId: z.string().optional().nullable(),
  customerCode: z.string().optional().nullable(),
  typeId: z.string().optional().nullable(),
  typeName: z.string().optional().nullable(),
  materialId: z.string().optional().nullable(),
  materialName: z.string().optional().nullable(),
  length: z.coerce.number().optional().nullable(),
  width: z.coerce.number().optional().nullable(),
  height: z.coerce.number().optional().nullable(),
  flatLength: z.coerce.number().optional().nullable(),
  flatWidth: z.coerce.number().optional().nullable(),
  unit: z.string(),
  quantity: z.coerce.number().optional().nullable(),
  unitPrice: z.coerce.number().optional().nullable(),
  costPrice: z.coerce.number().optional().nullable(),
  amount: z.coerce.number().optional().nullable(),
  poReference: z.string().optional().nullable(),
  soReference: z.string().optional().nullable(),
  customerPo: z.string().optional().nullable(),
  deliveryDate: z.string().optional().nullable(),
  deliveryDeadline: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  specs: z.string().optional().nullable(),
  missingFields: z.array(z.string()).default([]),
  isValid: z.boolean(),
});

export const commitImportBatchSchema = z.object({
  items: z.array(stagingItemSchema),
  overwriteMasterData: z.boolean().default(false),
  customerId: z.string().optional().nullable(),
  scope: z.enum(['ORDER', 'CATALOG']).default('ORDER'),
});

export const receiveToolingSchema = z.object({
  id: z.string().min(1),
  toolingId: z.string().min(1),
  location: z.string().optional(),
  storageZone: z.string().optional(),
  storageRack: z.string().optional(),
  storageShelf: z.string().optional(),
  storageBin: z.string().optional(),
  notes: z.string().optional(),
});

export const getUnassignedOrderDetailsForPrepressSchema = z.object({
  customerId: z.string().optional().nullable(),
  search: z.string().optional().nullable(),
  excludeIds: z.array(z.string()).optional(),
}).optional();

export const batchAssignImpositionSchema = z.object({
  orderDetailIds: z.array(z.string().min(1)).optional(),
  prepressCode: z.string().min(1, 'Mã bài bình là bắt buộc'),
  prepressPaperSize: z.string().min(1, 'Khổ in là bắt buộc'),
  prepressUps: z.number().int().min(1).optional(),
  sheetsPerUp: z.number().int().min(1).optional(),
  prepressPrintSheets: z.number().int().min(1).optional(),
  totalPrintSheets: z.number().int().min(1).optional(),
  prepressNotes: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  layoutName: z.string().optional().nullable(),
  layoutImage: z.string().optional().nullable(),
  fileUrl: z.string().optional().nullable(),
  dieCutId: z.string().optional().nullable(),
  machineId: z.string().optional().nullable(),
  materialId: z.string().optional().nullable(),
  printLayoutId: z.string().optional().nullable(),
  markPrepressCompleted: z.boolean().optional(),
  items: z.array(z.object({
    orderDetailId: z.string().min(1),
    ups: z.number().int().min(1),
    sheetsPerUp: z.number().int().min(1).optional(),
    plannedQty: z.number().int().min(1).optional(),
    printSheets: z.number().int().optional(),
    notes: z.string().optional().nullable(),
  })).optional(),
});
